import React, { MouseEventHandler, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";

import { OutOfStockResolutionPreference } from "src/storefront/types/out_of_stock_resolution_preference";
import { OutOfStockResolutionSelector } from "src/storefront/components/out_of_stock_resolution_selector";

import { StreamlineIcon } from "src/components/icons";
import removeIconUrl from "images/icons/remove.svg";
import questionCircleIconUrl from "images/icons/question-circle.svg";
import synchronizeArrowSquareIconUrl from "images/icons/synchronize-arrows-square.svg";

export const ProductModalSubstitutionsInfo = ({
  allowSubstitutionPreferences,
  outOfStockResolutionPreference,
  onPreferenceSave,
}: {
  allowSubstitutionPreferences: boolean;
  outOfStockResolutionPreference: OutOfStockResolutionPreference;
  onPreferenceSave: (preference: OutOfStockResolutionPreference) => void;
}) => {
  const substitutionsDisclaimer = useMemo(() => {
    return (
      <div className="surface" data-testid="substitutions-disclaimer">
        <div className="content">
          <div>
            <section className="container">
              <h5 className="is-size-5">Substitutions</h5>
            </section>

            <section className="container">
              <p>If a product is unavailable — the person packing your order will do their best to find a substitute with
                the same value or less.</p>
            </section>

            <hr className="space-m-none"/>

            <section className="container">
              <p className="is-tiny-muted-text">Any substitution price difference will be refunded when the order is
                complete. If no substitutes are available then the full item value will be refunded. Refunds can take 5-7
                business days to process.</p>
            </section>

            <section className="container">
              <div className="is-flexbox justify-content-center">
                <button className="button is-full-width is-visible-above-tablet"
                        data-action="click->popover-sheet#toggleVisibility" aria-label="Close">
                  Close
                </button>
                <button className="button is-icon-only has-large-icon is-primary is-hidden-above-tablet"
                        data-action="click->popover-sheet#toggleVisibility" aria-label="Close">
                  <StreamlineIcon href={removeIconUrl} />
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }, [])

  const outOfStockResolutionPortal = useMemo(() => {
    const substitutionsPopoverContainer = document.getElementById("substitutions-popover")
    const outOfStockResolutionPopover = allowSubstitutionPreferences ? <OutOfStockResolutionSelector selectedOutOfStockResolutionPreference={outOfStockResolutionPreference} onPreferenceSave={onPreferenceSave} /> : substitutionsDisclaimer;

    return substitutionsPopoverContainer ? createPortal(outOfStockResolutionPopover, substitutionsPopoverContainer) : <></>;
  }, [allowSubstitutionPreferences, onPreferenceSave, outOfStockResolutionPreference, substitutionsDisclaimer])

  const openOutOfStockPreferencesPopover: MouseEventHandler = useCallback((e) => {
    e.stopPropagation();
    const substitutionsCalloutPopover = document.getElementById("substitutions-popover");

    if (substitutionsCalloutPopover) {
      substitutionsCalloutPopover.classList.add("is-active")
    }
  }, [])

  const substitutionsCallout = useMemo(() => {
    return (
      <section className="container space-ml-none" data-testid="substitutions-callout">
        <button
          className="callout has-coloured-icon has-border flex-grow-1 is-flexbox justify-content-between align-items-center"
          onClick={openOutOfStockPreferencesPopover}>
          <div className="stack">
            <div className="is-flexbox align-items-center is-flex-gap-xs">
              <StreamlineIcon href={synchronizeArrowSquareIconUrl} />
              <div className="title"><span>Substitutions</span></div>
            </div>

            <p className="is-tiny-muted-text space-mt-none">{outOfStockResolutionPreference.title}</p>
          </div>

          <StreamlineIcon href={questionCircleIconUrl} additionalClasses={{ "is-muted-text": true }} />
        </button>
      </section>
    );
  }, [openOutOfStockPreferencesPopover, outOfStockResolutionPreference.title])

  return (
    <>
      {outOfStockResolutionPortal}
      {substitutionsCallout}
    </>
  );
};
